import { useSnapshot } from 'valtio'
import { FtIcon } from './icons.tsx'
import { TUTOR_AVATAR } from '../../../shared/branding.ts'
import {
  FreeTalkStore,
  formatClock,
  plural,
  speechLabel,
} from '../freeTalkStore.ts'

function Skeleton() {
  return (
    <>
      <div className="ft-sk" style={{ width: '40%' }} />
      <div className="ft-sk" style={{ width: '75%', height: 18 }} />
      <div className="ft-sk" style={{ width: '90%' }} />
    </>
  )
}

export function SummaryScreen(props: {
  store: FreeTalkStore
  wordsEnabled: boolean
  onFinish: () => void
}) {
  const { store } = props
  const state = useSnapshot(store.state)
  const sum = state.summary
  const n = sum.userTurns

  if (sum.short) {
    return (
      <section className="ft-sum">
        <div className="ft-sumin">
          <div className="ft-sumtop">
            <img className="ft-miniorb" src={TUTOR_AVATAR} alt="" />
            Свободное общение
          </div>
          <div className="ft-hero">
            <div>
              <h1>Слишком коротко для разбора</h1>
              <p>
                {n
                  ? `Ты ответил ${n} ${plural(n, ['раз', 'раза', 'раз'])}. `
                  : ''}
                Поговори ещё немного: после трёх реплик мы разберём ошибки и
                соберём новые слова.
              </p>
            </div>
          </div>
          <div className="ft-sumactions">
            <button className="ft-btn sec" onClick={props.onFinish}>
              Завершить
            </button>
            <button
              className="ft-btn pri"
              onClick={() => {
                store.continueTalk()
              }}
            >
              <FtIcon name="mic" size={20} />
              Продолжить разговор
            </button>
          </div>
        </div>
      </section>
    )
  }

  const vocab = state.vocab
  const allOn =
    sum.words.length > 0 && sum.words.every((w) => vocab.includes(w.word))

  return (
    <section className="ft-sum">
      <div className="ft-sumin">
        <div className="ft-sumtop">
          <img className="ft-miniorb" src={TUTOR_AVATAR} alt="" />
          Свободное общение
        </div>
        <div className="ft-hero">
          <div>
            <h1>Хорошо поговорили!</h1>
            <p>
              Ты говорил {speechLabel(sum.speechSeconds)} и ответил {n}{' '}
              {plural(n, ['раз', 'раза', 'раз'])}. Вот что стоит забрать с собой
              из этого разговора.
            </p>
          </div>
          <div className="ft-stats">
            <div className="ft-stat">
              <b>{formatClock(sum.speechSeconds)}</b>
              <span>речи</span>
            </div>
            <div className="ft-stat">
              <b>{n}</b>
              <span>{plural(n, ['реплика', 'реплики', 'реплик'])}</span>
            </div>
          </div>
        </div>

        <div className={`ft-cols ${props.wordsEnabled ? '' : 'single'}`}>
          <section className="ft-panel">
            <h2>Что можно сказать лучше</h2>
            {sum.loading && (
              <>
                <p className="ft-loadnote">Собираем разбор…</p>
                <Skeleton />
                <Skeleton />
              </>
            )}
            {!sum.loading && sum.error && (
              <div className="ft-fail">
                <FtIcon name="warn" size={18} />
                Разбор не загрузился
                <button
                  onClick={() => {
                    store.retrySummary()
                  }}
                >
                  Повторить
                </button>
              </div>
            )}
            {!sum.loading && !sum.error && sum.mistakes.length === 0 && (
              <div className="ft-empty">Ошибок не нашли — отлично!</div>
            )}
            {!sum.loading &&
              !sum.error &&
              sum.mistakes.map((x, i) => (
                <div className="ft-mist" key={i}>
                  <div className="ft-said">
                    Ты сказал:<s lang="en">{x.said}</s>
                    <button
                      className="ft-said-play"
                      aria-label="Послушать свою запись"
                      title="Послушать свою запись"
                      onClick={() => {
                        store.playSaid(x.said)
                      }}
                    >
                      <FtIcon name="play" size={12} />
                    </button>
                  </div>
                  <div className="ft-better">
                    <span className="ft-ok">
                      <FtIcon name="check" size={14} stroke={3} />
                    </span>
                    <span lang="en">{x.better}</span>
                    <button
                      className="ft-say"
                      aria-label="Послушать, как правильно"
                      onClick={() => {
                        store.playUrl(x.audio_url, x.better)
                      }}
                    >
                      <FtIcon name="volume" size={18} />
                    </button>
                  </div>
                  <p className="ft-why">{x.explanation_ru}</p>
                </div>
              ))}
          </section>

          {props.wordsEnabled && (
            <section className="ft-panel">
              <h2>
                Новые слова
                {!sum.loading && !sum.error && sum.words.length > 0 && (
                  <button
                    className="ft-link"
                    onClick={() => {
                      store.addAllWords()
                    }}
                  >
                    {allOn ? 'Все в Словарике' : 'Добавить все'}
                  </button>
                )}
              </h2>
              {sum.loading && <Skeleton />}
              {!sum.loading && sum.error && (
                <p className="ft-foot">Слова появятся вместе с разбором.</p>
              )}
              {!sum.loading && !sum.error && sum.words.length === 0 && (
                <p className="ft-foot">Новых слов в этом разговоре не было.</p>
              )}
              {!sum.loading &&
                !sum.error &&
                sum.words.map((w) => {
                  const on = state.vocab.includes(w.word)
                  return (
                    <div className="ft-word" key={w.word}>
                      <div>
                        <b lang="en">{w.word}</b>
                        <span>{w.translation}</span>
                      </div>
                      <button
                        className={`ft-add ${on ? 'on' : ''}`}
                        aria-pressed={on}
                        onClick={() => {
                          store.toggleWord(w.word)
                        }}
                      >
                        <FtIcon
                          name={on ? 'check' : 'plus'}
                          size={15}
                          stroke={on ? 3 : 2.6}
                        />
                        {on ? 'В Словарике' : 'В Словарик'}
                      </button>
                    </div>
                  )
                })}
              {!sum.loading && !sum.error && sum.words.length > 0 && (
                <p className="ft-foot">
                  Слова попадут в Словарик, и мы напомним их повторить.
                </p>
              )}
            </section>
          )}
        </div>

        <div className="ft-sumactions">
          <button
            className="ft-btn sec"
            onClick={() => {
              store.continueTalk()
            }}
          >
            <FtIcon name="mic" size={20} />
            Продолжить разговор
          </button>
          <button className="ft-btn pri" onClick={props.onFinish}>
            Завершить
          </button>
        </div>
      </div>
    </section>
  )
}
